import { TOPIC_NAMES } from 'crm-message-bus/types/MessageTopics';
import { useSendCrmMessageTopic } from 'crm-message-bus/useSendCrmMessageTopic';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { normalizeTypeId } from 'customer-data-objects/constants/ObjectTypeIds';
import { Metrics } from 'foundations-components/transitional/utils/Metrics';
import { AssociatedObjectsCardContext } from '../../context/AssociatedObjectsCardContext';
import { useAssociatedObjectsCardDataContext } from '../../context/AssociatedObjectsCardDataContext';
import { useSkeletonState } from '../../hooks/useSkeletonState';
import AssociatedObjectsCardTitle from './AssociatedObjectsCardTitle';
import { jsx as _jsx } from "react/jsx-runtime";
function AssociatedObjectsCardTitleSdkDataProvider({
  cardId,
  hasScope,
  isUngated,
  objectId,
  objectTypeId,
  propertyNames,
  sortConfiguration,
  readOnly,
  title,
  toObjectTypeId,
  propertySource
}) {
  const [skeletonState, setSkeletonState] = useSkeletonState({
    cardId,
    objectId,
    objectTypeId
  });
  const hasReportedSkeletonData = useRef(false);
  const propertySourceInfo = useMemo(() => {
    return {
      propertySource
    };
  }, [propertySource]);
  // TODO: replace isEmbedded with real info from crm-sdk once they are ready to provide it
  const isEmbedded = false;
  const sendUpdateObject = useSendCrmMessageTopic(TOPIC_NAMES.UPDATE_OBJECT);
  const sendObjectCreated = useSendCrmMessageTopic(TOPIC_NAMES.OBJECT_CREATED);
  const sendUpdateAssociation = useSendCrmMessageTopic(TOPIC_NAMES.UPDATE_ASSOCIATIONS);
  const onObjectUpdate = useCallback(({
    properties
  }) => {
    sendUpdateObject({
      propertyNames: properties.map((_val, key) => key).toArray(),
      objectTypeId,
      objectId: String(objectId)
    });
  }, [objectId, objectTypeId, sendUpdateObject]);
  const onAssociationUpdate = useCallback(args => {
    const {
      objectId: updatedObjectId,
      objectType: updatedObjectType,
      associationObjectType
    } = args;
    sendUpdateAssociation({
      objectId: String(updatedObjectId),
      objectTypeId: normalizeTypeId(updatedObjectType),
      toObjectTypeId: normalizeTypeId(associationObjectType)
    });
  }, [sendUpdateAssociation]);
  const onObjectCreate = useCallback(args => {
    const {
      associationType,
      objectId: createdObjectId,
      addAnother
    } = args;
    sendObjectCreated({
      objectId: String(createdObjectId),
      objectTypeId: normalizeTypeId(associationType),
      preventRedirect: !!addAnother
    });
  }, [sendObjectCreated]);
  const {
    associationsData,
    associationTypesData,
    objectTypePermissions,
    primaryDisplayLabel,
    refetchAssociationsData,
    userPermissions,
    refetchObjectData
  } = useAssociatedObjectsCardDataContext();
  useEffect(() => {
    if (associationsData !== null && associationsData !== void 0 && associationsData.impliedUnlabeledAssociationObjects && ((associationsData === null || associationsData === void 0 ? void 0 : associationsData.impliedUnlabeledAssociationObjects.total) !== (skeletonState === null || skeletonState === void 0 ? void 0 : skeletonState.value) || !hasReportedSkeletonData.current)) {
      if (!hasReportedSkeletonData.current) {
        hasReportedSkeletonData.current = true;
        Metrics.counter('skeleton.loaded', {
          difference: ((associationsData === null || associationsData === void 0 ? void 0 : associationsData.impliedUnlabeledAssociationObjects.total) - ((skeletonState === null || skeletonState === void 0 ? void 0 : skeletonState.value) || 0)).toString(),
          isSkeletonStateUndefined: (skeletonState === undefined).toString()
        }).increment();
      }
      setSkeletonState(associationsData === null || associationsData === void 0 ? void 0 : associationsData.impliedUnlabeledAssociationObjects.total);
    }
  }, [associationsData === null || associationsData === void 0 ? void 0 : associationsData.impliedUnlabeledAssociationObjects, setSkeletonState, skeletonState]);
  const contextValue = useMemo(() => ({
    associationsData: {
      impliedUnlabeledAssociationObjects: associationsData === null || associationsData === void 0 ? void 0 : associationsData.impliedUnlabeledAssociationObjects,
      primaryAssociationObject: associationsData === null || associationsData === void 0 ? void 0 : associationsData.primaryAssociationObject,
      associationsDataError: associationsData === null || associationsData === void 0 ? void 0 : associationsData.associationsDataError
    },
    associationTypesData: associationTypesData,
    cardId,
    hasScope,
    isEmbedded,
    isUngated,
    objectId,
    objectTypeId,
    objectTypePermissions: objectTypePermissions,
    onAssociationUpdate,
    onObjectCreate,
    onObjectUpdate,
    primaryDisplayLabel: primaryDisplayLabel,
    propertyNames,
    propertySource: propertySourceInfo,
    readOnly,
    refetchAssociationsData,
    refetchObjectData,
    sortConfiguration,
    title,
    toObjectTypeId,
    userPermissions: userPermissions
  }), [associationTypesData, associationsData === null || associationsData === void 0 ? void 0 : associationsData.associationsDataError, associationsData === null || associationsData === void 0 ? void 0 : associationsData.impliedUnlabeledAssociationObjects, associationsData === null || associationsData === void 0 ? void 0 : associationsData.primaryAssociationObject, cardId, hasScope, isEmbedded, isUngated, objectId, objectTypeId, objectTypePermissions, onAssociationUpdate, onObjectCreate, onObjectUpdate, primaryDisplayLabel, propertyNames, propertySourceInfo, readOnly, refetchAssociationsData, refetchObjectData, sortConfiguration, title, toObjectTypeId, userPermissions]);
  return /*#__PURE__*/_jsx(AssociatedObjectsCardContext.Provider, {
    value: contextValue,
    children: /*#__PURE__*/_jsx(AssociatedObjectsCardTitle, {})
  });
}
export default AssociatedObjectsCardTitleSdkDataProvider;